import { Shield, FileCheck, IndianRupee, Stethoscope, Paperclip, MessageSquare } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    claimId: string;
    policyNumber: string;
    insurerName: string;
    planName: string;
    claimType: string;
    claimAmount: number;
    approvedAmount?: number;
    claimStatus: string;
    admissionDate?: string;
    dischargeDate?: string;
    diagnosis: string[];
    procedures?: string[];
    documents: { name: string; type: string; status: string }[];
    remarks?: string;
    signatories: {
      name: string;
      role: string;
      nhcId?: string;
      licenseNo?: string;
      institution?: string;
    }[];
  };
}

function formatCurrency(amount: number): string {
  return `Rs. ${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getStatusStyle(status: string) {
  const s = (status || '').toUpperCase();
  if (s === 'APPROVED' || s === 'SETTLED') return 'bg-green-100 text-green-700 border-green-300';
  if (s === 'PENDING' || s === 'UNDER REVIEW') return 'bg-yellow-100 text-yellow-700 border-yellow-300';
  if (s === 'REJECTED' || s === 'DENIED') return 'bg-red-100 text-red-700 border-red-300';
  return 'bg-gray-100 text-gray-700 border-gray-300';
}

function getDocStatusStyle(status: string) {
  const s = (status || '').toUpperCase();
  if (s === 'VERIFIED') return 'bg-green-100 text-green-700';
  if (s === 'PENDING') return 'bg-yellow-100 text-yellow-700';
  if (s === 'MISSING') return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-700';
}

export default function InsuranceClaimPackTemplate({ data }: Props) {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-gray-700" />
          <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
            Insurance Claim Pack
          </span>
        </div>
        <span
          className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded border ${getStatusStyle(data.claimStatus)}`}
        >
          {data.claimStatus}
        </span>
      </div>

      {/* Claim Summary Grid */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 bg-gray-50 px-3 py-2.5 border border-gray-200 rounded text-[10px]">
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Claim ID:</span>
          <span className="text-gray-800 font-mono">{data.claimId}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Policy No:</span>
          <span className="text-gray-800 font-mono">{data.policyNumber}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Insurer:</span>
          <span className="text-gray-800">{data.insurerName}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Plan:</span>
          <span className="text-gray-800">{data.planName}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Claim Type:</span>
          <span className="text-gray-800">{data.claimType}</span>
        </div>
      </div>

      {/* Financial Section */}
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <IndianRupee className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Financial Summary
          </h3>
        </div>
        <div className="bg-gray-50 px-3 py-2.5 border border-gray-200 rounded text-[10px]">
          <div className="flex justify-between py-1 border-b border-gray-200">
            <span className="font-semibold text-gray-600">Claimed Amount</span>
            <span className="text-gray-800 font-mono font-medium">{formatCurrency(data.claimAmount)}</span>
          </div>
          {data.approvedAmount !== undefined && (
            <div className="flex justify-between py-1 border-b border-gray-200">
              <span className="font-semibold text-gray-600">Approved Amount</span>
              <span className="text-green-700 font-mono font-bold">{formatCurrency(data.approvedAmount)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Clinical Details */}
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <Stethoscope className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Clinical Details
          </h3>
        </div>
        <div className="bg-gray-50 px-3 py-2.5 border border-gray-200 rounded text-[10px] space-y-1.5">
          {data.admissionDate && (
            <div className="flex gap-2">
              <span className="font-semibold text-gray-600">Admission Date:</span>
              <span className="text-gray-800">{data.admissionDate}</span>
            </div>
          )}
          {data.dischargeDate && (
            <div className="flex gap-2">
              <span className="font-semibold text-gray-600">Discharge Date:</span>
              <span className="text-gray-800">{data.dischargeDate}</span>
            </div>
          )}
          <div>
            <span className="font-semibold text-gray-600">Diagnosis:</span>
            <ul className="list-disc list-inside mt-0.5 ml-1 space-y-0.5 text-gray-700">
              {(data.diagnosis || []).map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </ul>
          </div>
          {data.procedures && data.procedures.length > 0 && (
            <div>
              <span className="font-semibold text-gray-600">Procedures:</span>
              <ul className="list-disc list-inside mt-0.5 ml-1 space-y-0.5 text-gray-700">
                {data.procedures.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Attached Documents */}
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <Paperclip className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Attached Documents
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[10px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Document Name
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Type
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {(data.documents || []).map((doc, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-800 font-medium">
                    {doc.name}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-700">
                    {doc.type}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200">
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${getDocStatusStyle(doc.status)}`}>
                      {doc.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Remarks */}
      {data.remarks && (
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <MessageSquare className="w-3 h-3 text-gray-600" />
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
              Remarks
            </h3>
          </div>
          <p className="text-[10px] text-gray-700 leading-relaxed bg-gray-50 px-3 py-2 border border-gray-200 rounded">
            {data.remarks}
          </p>
        </div>
      )}

      {/* Digital Signatures */}
      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
